import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Skeleton,
  Stack,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceIcon from "@mui/icons-material/Place";
import announcementServices from "../../services/announcementServices";
import { fDateTime } from "../../utils";
import ImageWithFallback from "../../components/ImageWithFallback";

const StyledCard = styled(Card)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
});

const StyledCardMedia = styled(CardMedia)({
  height: 420,
  objectFit: "cover",
});

const getFileType = (url) => url?.split(".").pop()?.toLowerCase();

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const PER_PAGE = 15;

const AnnouncementsList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    announcementServices
      .get()
      .then((res) => setData(res?.data?.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const sorted = [...data].sort((a, b) => {
    const dateA = new Date(a?.date || a?.createdAt || 0);
    const dateB = new Date(b?.date || b?.createdAt || 0);
    return dateB - dateA;
  });

  const totalPages = Math.ceil(sorted.length / PER_PAGE) || 1;
  const currentPage = Math.min(page, totalPages);
  const paginated = sorted.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ pt: 3, pb: 6 }}>
        <Container maxWidth="lg">
          <h1 style={{ fontSize: "2.5rem", fontWeight: 700, color: "rgba(26,128,200)", textAlign: "center", marginBottom: "0.5rem", marginTop: "2rem" }}>
            Announcements
          </h1>
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 1 }} />
                <Skeleton height={32} sx={{ mt: 1 }} />
                <Skeleton height={24} width="60%" />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, pb: 6, minHeight: "60vh" }}>
      <Container maxWidth="lg">
        <h1 style={{ fontSize: "2.5rem", fontWeight: 700, color: "rgba(26,128,200)", textAlign: "center", marginBottom: "0.5rem", marginTop: "2rem" }}>Announcements</h1>
        <p style={{ textAlign: "center", marginBottom: "2.5rem", marginTop: 0, color: "#666", fontSize: "1rem" }}>
          Stay updated with our latest news and events
        </p>

        {sorted.length === 0 ? (
          <Typography align="center" sx={{ py: 4, color: "#666" }}>
            No announcements yet.
          </Typography>
        ) : (
          <>
          <Grid container spacing={3}>
            {paginated.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item._id}>
                <StyledCard>
                  <CardActionArea component={Link} to={`/announcements/${item._id}`}>
                    <StyledCardMedia>
                      {getFileType(item?.url) === "pdf" ? (
                        <img
                          src="/pdf.png"
                          alt={item.title}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <ImageWithFallback
                          path={item?.url}
                          alt={item.title}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </StyledCardMedia>
                    <CardContent sx={{ flexGrow: 1, textAlign: "left" }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom noWrap sx={{ color: "#000" }}>
                        {item.title}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#666", fontSize: "0.875rem" }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: "#666" }} />
                        <span style={{ color: "#666" }}>{fDateTime(item?.date)}</span>
                      </Stack>
                      {item?.location && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#666", fontSize: "0.875rem", mt: 0.5 }}>
                          <PlaceIcon sx={{ fontSize: 16, color: "#666" }} />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#666" }}>{item.location}</span>
                        </Stack>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: "#666",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {(() => {
                          const t = stripHtml(item?.description).slice(0, 180);
                          return t.length >= 180 ? `${t}...` : t || "View details →";
                        })()}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, p) => setPage(p)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default AnnouncementsList;
